import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  Copy, ExternalLink, Pencil, Plus, RefreshCw, Trash2,
  MessageSquare, X, CheckCircle2, AlertCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';

/* ── constants ──────────────────────────────────────────────── */
const CATEGORIES = [
  { value: 'order',    label: 'Order',    emoji: '📦', color: 'blue'   },
  { value: 'payment',  label: 'Payment',  emoji: '💳', color: 'emerald'},
  { value: 'reminder', label: 'Reminder', emoji: '⏰', color: 'amber'  },
  { value: 'invoice',  label: 'Invoice',  emoji: '🧾', color: 'violet' },
  { value: 'delivery', label: 'Delivery', emoji: '🚚', color: 'teal'   },
  { value: 'thank_you',label: 'Thank You',emoji: '🙏', color: 'rose'   },
];

const VARIABLES = [
  { name: 'customer_name', label: 'Customer Name',  example: 'Jane Doe' },
  { name: 'amount',        label: 'Amount',          example: '50,000' },
  { name: 'currency',      label: 'Currency',        example: 'UGX' },
  { name: 'payment_link',  label: 'Payment Link',    example: 'https://pay.jjuma.com/…' },
  { name: 'invoice_number',label: 'Invoice No.',     example: 'INV-001' },
  { name: 'order_number',  label: 'Order No.',       example: 'ORD-001' },
  { name: 'due_date',      label: 'Due Date',        example: '2026-05-31' },
  { name: 'reason',        label: 'Reason',          example: 'Payment' },
];

const EMPTY_FORM = {
  title: '',
  message: '',
  category: 'order',
  variables: ['customer_name', 'amount', 'currency', 'payment_link'],
  is_active: true,
};

const CAT_COLORS = {
  blue:    { bg: 'bg-blue-50 text-blue-700 border-blue-100' },
  emerald: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  amber:   { bg: 'bg-amber-50 text-amber-700 border-amber-100' },
  violet:  { bg: 'bg-violet-50 text-violet-700 border-violet-100' },
  teal:    { bg: 'bg-teal-50 text-teal-700 border-teal-100' },
  rose:    { bg: 'bg-rose-50 text-rose-700 border-rose-100' },
};

function renderMessage(message, values) {
  let output = String(message || '');
  VARIABLES.forEach(({ name, example }) => {
    output = output.replaceAll(`{${name}}`, values?.[name] ?? example ?? '');
  });
  return output.replace(/\{[a-zA-Z0-9_]+\}/g, '').replace(/\s{2,}/g, ' ').trim();
}

function getCategoryInfo(value) {
  return CATEGORIES.find(c => c.value === value) || CATEGORIES[0];
}

/* ── WhatsApp bubble preview ────────────────────────────────── */
function WhatsAppBubble({ message }) {
  if (!message) return null;
  return (
    <div className="bg-[#ECE5DD] rounded-2xl p-4 min-h-[80px]">
      <div className="bg-white rounded-[14px] rounded-tl-none px-4 py-3 shadow-sm max-w-[85%] relative">
        <div className="absolute -left-2 top-0 w-0 h-0 border-t-[12px] border-r-[12px] border-t-white border-r-transparent" />
        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{message}</p>
        <p className="text-[10px] text-slate-400 text-right mt-1">12:00 PM ✓✓</p>
      </div>
    </div>
  );
}

/* ── Template card ──────────────────────────────────────────── */
function TemplateCard({ template, onEdit, onToggle, onDelete, preview }) {
  const cat   = getCategoryInfo(template.category);
  const clr   = CAT_COLORS[cat.color] || CAT_COLORS.blue;
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      <div className="px-4 py-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="text-2xl mt-0.5 shrink-0">{cat.emoji}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-slate-900 truncate">{template.title}</p>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${clr.bg}`}>
                {cat.label}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${template.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {template.is_active ? '● Active' : '○ Inactive'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              className="text-xs text-slate-400 hover:text-slate-600 mt-1 text-left line-clamp-1"
            >
              {template.message}
            </button>
          </div>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3">
          <WhatsAppBubble message={renderMessage(template.message, preview)} />
        </div>
      )}

      <div className="px-4 py-3 border-t border-slate-50 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setExpanded(e => !e)}
          className="border-slate-200 text-slate-600 text-xs h-8 px-3"
        >
          <Eye size={12} className="mr-1" /> {expanded ? 'Hide' : 'Preview'}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => onEdit(template)} className="border-slate-200 text-xs h-8 px-3">
          <Pencil size={12} className="mr-1" /> Edit
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => onToggle(template)} className="border-slate-200 text-xs h-8 px-3">
          {template.is_active ? 'Disable' : 'Enable'}
        </Button>
        {!template.is_default && (
          <Button type="button" size="sm" variant="outline" onClick={() => onDelete(template)} className="border-rose-100 text-rose-600 hover:bg-rose-50 text-xs h-8 px-3">
            <Trash2 size={12} className="mr-1" /> Delete
          </Button>
        )}
      </div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────── */
export default function WhatsAppTemplatesPage() {
  const [templates,       setTemplates]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [editingId,       setEditingId]       = useState('');
  const [form,            setForm]            = useState(EMPTY_FORM);
  const [filterCategory,  setFilterCategory]  = useState('all');
  const [showForm,        setShowForm]        = useState(false);
  const [preview,         setPreview]         = useState({
    customer_name: 'Jane Doe', amount: '50,000', currency: 'UGX',
    payment_link: 'https://pay.jjuma.com/pay/demo', invoice_number: 'INV-001',
    order_number: 'ORD-001', due_date: '2026-05-31', reason: 'Order payment',
    phone: '256700000000',
  });
  const [previewUrl,    setPreviewUrl]    = useState('');
  const [previewMsg,    setPreviewMsg]    = useState('');
  const [blockedMessage,setBlockedMessage] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/whatsapp-templates');
      setTemplates(r.data?.templates || []);
      setBlockedMessage('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        setBlockedMessage(detail || 'Upgrade your package to use WhatsApp templates.');
        setTemplates([]);
      } else toast.error(detail || 'Failed to load templates');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const startEdit = (template) => {
    setEditingId(template.id);
    setForm({
      title: template.title || '', message: template.message || '',
      category: template.category || 'order',
      variables: Array.isArray(template.variables) ? template.variables : [],
      is_active: template.is_active !== false,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => { setEditingId(''); setForm(EMPTY_FORM); setShowForm(false); setPreviewMsg(''); setPreviewUrl(''); };

  const toggleVariable = (name) => {
    setForm(prev => {
      const current = Array.isArray(prev.variables) ? prev.variables : [];
      return current.includes(name)
        ? { ...prev, variables: current.filter(item => item !== name) }
        : { ...prev, variables: [...current, name] };
    });
  };

  const currentMessage = useMemo(() => renderMessage(form.message, preview), [form.message, preview]);

  const generatePreviewLink = () => {
    const encoded = encodeURIComponent(currentMessage);
    const phone   = String(preview.phone || '').trim();
    setPreviewMsg(currentMessage);
    setPreviewUrl(phone ? `https://wa.me/${phone.replace(/\D/g, '')}?text=${encoded}` : `https://wa.me/?text=${encoded}`);
    toast.success('Preview generated');
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { title: form.title, message: form.message, category: form.category, variables: form.variables, is_active: !!form.is_active };
      if (editingId) { await api.put(`/dashboard/whatsapp-templates/${editingId}`, payload); toast.success('Template updated'); }
      else { await api.post('/dashboard/whatsapp-templates', payload); toast.success('Template created'); }
      resetForm();
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save template');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (template) => {
    try {
      await api.patch(`/dashboard/whatsapp-templates/${template.id}/status`, { is_active: !template.is_active });
      toast.success('Template status updated'); await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update template'); }
  };

  const deleteTemplate = async (template) => {
    if (!window.confirm(`Delete template "${template.title}"?`)) return;
    try {
      await api.delete(`/dashboard/whatsapp-templates/${template.id}`);
      toast.success('Template deleted');
      if (editingId === template.id) resetForm();
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete template'); }
  };

  const copyPreview = async () => {
    const txt = previewUrl || previewMsg;
    if (!txt) { generatePreviewLink(); return; }
    try { await navigator.clipboard.writeText(txt); toast.success('Copied!');
    } catch { toast.error('Could not copy'); }
  };

  /* ── Filtered templates ──── */
  const filtered = filterCategory === 'all' ? templates : templates.filter(t => t.category === filterCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-7 h-7 border-[3px] border-emerald-700 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-400">Loading templates…</p>
        </div>
      </div>
    );
  }

  if (blockedMessage) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>WhatsApp Templates</h1>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-bold text-amber-900">Feature Not Available</p>
            <p className="text-sm text-amber-800 mt-1">{blockedMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            WhatsApp Templates
          </h1>
          <p className="text-slate-500 text-sm mt-1">Create reusable message templates for invoices, orders, reminders and more.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-slate-200 gap-1.5">
            <RefreshCw size={14} /> Refresh
          </Button>
          <Button
            className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5"
            onClick={() => { resetForm(); setShowForm(true); }}
          >
            <Plus size={15} /> New Template
          </Button>
        </div>
      </div>

      {/* ── Create / edit form ──────────────────────────────────── */}
      {showForm && (
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardHeader className="pb-4 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <MessageSquare size={16} className="text-emerald-600" />
              {editingId ? 'Edit Template' : 'Create New Template'}
            </CardTitle>
            <button type="button" onClick={resetForm} className="text-slate-400 hover:text-slate-600 transition-colors">
              <X size={18} />
            </button>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
              {/* Form */}
              <form onSubmit={save} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Template Title</Label>
                    <Input value={form.title} onChange={e => update('title', e.target.value)} className="mt-1 rounded-xl border-slate-200 text-sm" placeholder="Payment reminder" required />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Category</Label>
                    <Select value={form.category} onValueChange={v => update('category', v)}>
                      <SelectTrigger className="mt-1 rounded-xl border-slate-200 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(item => (
                          <SelectItem key={item.value} value={item.value}>{item.emoji} {item.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs font-semibold text-slate-600">Message</Label>
                  <Textarea
                    value={form.message}
                    onChange={e => update('message', e.target.value)}
                    className="mt-1 rounded-xl border-slate-200 text-sm resize-none"
                    rows={5}
                    placeholder={`Hi {customer_name}, your payment of {currency} {amount} is due on {due_date}.\n\nPay here: {payment_link}`}
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">Use variables like {'{customer_name}'}, {'{amount}'}, {'{payment_link}'}</p>
                </div>

                {/* Variables */}
                <div>
                  <Label className="text-xs font-semibold text-slate-600 mb-2 block">Available Variables</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {VARIABLES.map(({ name }) => (
                      <label
                        key={name}
                        className={`flex items-center gap-1.5 rounded-xl border px-2.5 py-2 text-xs font-semibold cursor-pointer transition-colors ${
                          form.variables?.includes(name)
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                            : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <input type="checkbox" className="sr-only" checked={form.variables?.includes(name)} onChange={() => toggleVariable(name)} />
                        {form.variables?.includes(name) ? <CheckCircle2 size={11} className="text-emerald-600" /> : <div className="w-3 h-3 rounded border border-slate-300" />}
                        {`{${name}}`}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-bold text-slate-900">Active Template</p>
                    <p className="text-xs text-slate-500">Inactive templates stay saved but are hidden from quick use.</p>
                  </div>
                  <Switch checked={!!form.is_active} onCheckedChange={v => update('is_active', v)} />
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving}>
                    {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Template')}
                  </Button>
                  <Button type="button" variant="outline" className="border-slate-200" onClick={resetForm}>Cancel</Button>
                </div>
              </form>

              {/* Live preview panel */}
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Preview Values</p>
                  <div className="grid gap-2">
                    {[
                      { key: 'customer_name', label: 'Customer Name' },
                      { key: 'amount',        label: 'Amount' },
                      { key: 'currency',      label: 'Currency' },
                      { key: 'phone',         label: 'Phone' },
                    ].map(({ key, label }) => (
                      <div key={key}>
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{label}</label>
                        <input
                          type="text"
                          value={preview[key] || ''}
                          onChange={e => setPreview(p => ({ ...p, [key]: e.target.value }))}
                          className="mt-0.5 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-900 outline-none focus:border-emerald-500"
                        />
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" onClick={generatePreviewLink}>
                    Generate Preview
                  </Button>
                </div>

                {previewMsg && (
                  <div className="rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="bg-[#075E54] px-4 py-2 flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{(preview.customer_name || 'J').charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-white">{preview.customer_name || 'Customer'}</p>
                        <p className="text-[10px] text-green-300">online</p>
                      </div>
                    </div>
                    <div className="p-3">
                      <WhatsAppBubble message={previewMsg} />
                    </div>
                    <div className="px-3 pb-3 flex gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={copyPreview} className="flex-1 border-slate-200 text-xs h-8">
                        <Copy size={11} className="mr-1" /> Copy
                      </Button>
                      {previewUrl && (
                        <Button type="button" size="sm" className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-white text-xs h-8" onClick={() => window.open(previewUrl, '_blank', 'noopener,noreferrer')}>
                          <ExternalLink size={11} className="mr-1" /> Open WhatsApp
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Category filter ──────────────────────────────────── */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        <button
          type="button"
          onClick={() => setFilterCategory('all')}
          className={`shrink-0 rounded-xl border px-3 py-1.5 text-xs font-bold transition-all ${filterCategory === 'all' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
        >
          All ({templates.length})
        </button>
        {CATEGORIES.map(cat => {
          const count = templates.filter(t => t.category === cat.value).length;
          if (!count) return null;
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => setFilterCategory(cat.value)}
              className={`shrink-0 flex items-center gap-1 rounded-xl border px-3 py-1.5 text-xs font-bold transition-all ${
                filterCategory === cat.value
                  ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {cat.emoji} {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* ── Template grid ─────────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center rounded-2xl border border-dashed border-slate-200">
          <MessageSquare size={40} className="mx-auto text-slate-200 mb-3" />
          <p className="text-sm font-bold text-slate-500">No templates yet</p>
          <p className="text-xs text-slate-400 mt-1">Default templates are created automatically when you first use this feature.</p>
          <Button className="mt-4 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setShowForm(true)}>
            <Plus size={14} className="mr-1.5" /> Create Your First Template
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={startEdit}
              onToggle={toggleStatus}
              onDelete={deleteTemplate}
              preview={preview}
            />
          ))}
        </div>
      )}
    </div>
  );
}
